import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import axios from 'axios';

// Create and export the context
export const ChatContext = createContext(null);

// Export the hook
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

// Export the provider component
export const ChatProvider = ({ children }) => {
  // Initialize all state variables properly
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatLocked, setIsChatLocked] = useState(false);
  const [activeChat, setActiveChat] = useState(() => {
    const savedChat = localStorage.getItem('activeChat');
    return savedChat ? JSON.parse(savedChat) : null;
  });
  const [chatGroups, setChatGroups] = useState([]);
  const [privateChats, setPrivateChats] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isActiveChatLoading, setIsActiveChatLoading] = useState(false);
  const activeRequestRef = useRef(null);

  const apiUrl = process.env.REACT_APP_STATUS_API_URL;

  // Hent chat-grupper fra backend med bedre type validering,
  const fetchChatGroups = useCallback(async () => {
    const token = Cookies.get("token");
    if (!token) return;
    try {
      const response = await fetch(`${apiUrl}/chat/groups`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.groups || !Array.isArray(data.groups)) {
          console.error("Invalid groups data format:", data);
          setChatGroups([]);
          return;
        }

        // Deduplicate and ensure proper type
        const uniqueGroups = [];
        const seenGroupIds = new Set();

        data.groups.forEach(group => {
          // Skip if null, invalid or duplicates
          if (!group || typeof group !== 'object' || !group.id || seenGroupIds.has(group.id)) {
            return;
          }

          // Ensure it's a group type, not a private chat
          if (group.type && group.type !== 'one-to-one') {
            seenGroupIds.add(group.id);

            // Ensure it has a proper type property
            uniqueGroups.push({
              ...group,
              type: group.type || 'group'
            });
          }
        });

        console.log(`After filtering: ${uniqueGroups.length} valid groups`);
        setChatGroups(uniqueGroups);
      }
    } catch (error) {
      console.error("Error fetching chat groups:", error);
    }
  }, [apiUrl]);

  // Hent private samtaler (one-to-one chatter),
  const fetchPrivateChats = useCallback(async () => {
    const token = Cookies.get("token");
    if (!token) return;
    try {
      const response = await fetch(`${apiUrl}/chat/private-chats`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.chats || !Array.isArray(data.chats)) {
          console.error("Invalid private chats data format:", data);
          setPrivateChats([]);
          return;
        }

        // Deduplicate and validate
        const uniqueChats = [];
        const seenChatIds = new Set();

        data.chats.forEach(chat => {
          // Skip if null, invalid or duplicate
          if (!chat || typeof chat !== 'object' || !chat.id || seenChatIds.has(chat.id)) {
            return;
          }

          seenChatIds.add(chat.id);

          // Always ensure private chats have the right type
          uniqueChats.push({
            ...chat,
            type: 'one-to-one'
          });
        });

        console.log(`After filtering: ${uniqueChats.length} valid private chats`);
        setPrivateChats(uniqueChats);
      }
    } catch (error) {
      console.error("Error fetching private chats:", error);
    }
  }, [apiUrl]);

  // Start ny privat chat
  const startPrivateChat = async (username) => {
    const token = Cookies.get("token");
    if (!token) return;

    try {
      console.log("Starting private chat with:", username);

      // Sjekk først om chatten allerede eksisterer
      const existingChat = privateChats.find(chat => 
        chat.participants && 
        Array.isArray(chat.participants) && 
        chat.participants.includes(username)
      );

      if (existingChat) {
        console.log("Found existing chat:", existingChat);
        return existingChat;
      }

      const response = await fetch(`${apiUrl}/chat/start-private`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("New chat created:", data.chat);

        // Ensure the chat has the correct type
        const newChat = {
          ...data.chat,
          type: 'one-to-one'
        };

        setPrivateChats(prev => [...prev, newChat]);
        return newChat;
      } else {
        console.error("Failed to create chat:", await response.text());
      }
    } catch (error) {
      console.error("Error starting private chat:", error);
    }
  };

  // Bytt aktiv chat
  const switchChat = useCallback((chatInfo) => {
    console.log("Switching to chat:", {
      id: chatInfo.id,
      type: chatInfo.type,
      name: chatInfo.name
    });

    // Lagre i localStorage
    localStorage.setItem('activeChat', JSON.stringify(chatInfo));

    // Cancel any pending requests for previous chat
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    // Create new AbortController for this chat
    activeRequestRef.current = new AbortController();

    setIsActiveChatLoading(true);
    setActiveChat(chatInfo);
    setUnreadMessages(prev => ({ ...prev, [chatInfo.id]: 0 }));

    // Return the abort controller signal for use in components
    return activeRequestRef.current.signal;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, []);

  // Håndter nye meldinger når chatten ikke er aktiv
  const handleNewMessage = (chatId) => {
    if (activeChat?.id !== chatId) {
      setUnreadMessages(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || 0) + 1
      }));
    }
  };

  const toggleChat = () => {
    if (isChatLocked) {
      setIsChatLocked(false);
      setIsChatOpen(false);
    } else {
      setIsChatOpen(prev => !prev);
    }
  };

  const lockChat = () => {
    setIsChatLocked(prev => !prev);
  };

  const leaveGroup = async (groupId) => {
    const token = Cookies.get("token");
    if (!token) return false;

    try {
      // Immediately remove the group from local state for responsive UI
      setChatGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
      
      // If this was the active chat, clear it
      if (activeChat?.id === groupId) {
        setActiveChat(null);
        setIsChatOpen(false);
      }
      
      // Now make the API call
      const endpoint = `${process.env.REACT_APP_STATUS_API_URL}/chat/leave-group`;
      console.log(`Attempting to leave group ${groupId} using endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ group_id: groupId }),
      });

      const responseText = await response.text();
      console.log(`Server response (${response.status}):`, responseText);

      if (response.ok) {
        console.log(`Successfully left group: ${groupId}`);
        // We've already updated the UI optimistically, but fetch fresh data to ensure consistency
        fetchChatGroups();
        return true;
      }

      // If the API call fails, restore the group in our state
      console.error(`Failed to leave group. Status: ${response.status}, Response: ${responseText}`);
      fetchChatGroups();
      return false;
    } catch (error) {
      console.error("Error details:", error);
      // Fetch groups again to restore state in case of error
      fetchChatGroups();
      return false;
    }
  };

  const deletePrivateChat = async (groupId) => {
    const token = Cookies.get("token");
    if (!token) return false;

    try {
      // Immediately remove the chat from local state for responsive UI
      setPrivateChats(prevChats => prevChats.filter(chat => chat.id !== groupId));
      
      // If this was the active chat, clear it
      if (activeChat?.id === groupId) {
        setActiveChat(null);
        setIsChatOpen(false);
      }
      
      // Now make the API call
      const endpoint = `${process.env.REACT_APP_STATUS_API_URL}/chat/delete-private-chat`;
      console.log(`Attempting to delete private chat using endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ group_id: groupId }),
      });

      if (response.ok) {
        console.log(`Successfully deleted private chat: ${groupId}`);
        // We've already updated the UI optimistically, but fetch fresh data to ensure consistency
        fetchPrivateChats();
        return true;
      }

      // If the API call fails, restore the chat in our state
      const errorText = await response.text();
      console.error(`Failed to delete private chat. Status: ${response.status}, Response: ${errorText}`);
      fetchPrivateChats();
      return false;
    } catch (error) {
      console.error("Error deleting private chat:", error);
      // Fetch chats again to restore state in case of error
      fetchPrivateChats();
      return false;
    }
  };

  // Add createGroupChat function to the ChatContext provider
  const createGroupChat = async ({ name, description, participants }) => {
    try {
      setIsLoading(true);
      const token = Cookies.get('token');
      console.log("Creating group chat:", { name, description, participants });
  
      // Make API call to create a group chat
      const response = await axios.post(
        `${process.env.REACT_APP_STATUS_API_URL}/chat/create_group`,
        {
          name,
          description,
          participants,
          type: 'group'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      console.log("Group creation response:", response.data);
  
      if (response.data && response.data.group) {
        // Add the new group to chatGroups
        const newGroup = response.data.group;
        setChatGroups(prevGroups => [newGroup, ...prevGroups]);
        
        // Set it as the active chat
        setActiveChat(newGroup);
        return newGroup;
      }
      return null;
    } catch (error) {
      console.error('Error creating group chat:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Last inn chat-data ved oppstart
  React.useEffect(() => {
    fetchChatGroups();
    fetchPrivateChats();
  }, [fetchChatGroups, fetchPrivateChats]);

  const clearActiveChat = useCallback(() => {
    localStorage.removeItem('activeChat');
    setActiveChat(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        setIsChatOpen,
        isChatLocked,
        toggleChat,
        lockChat,
        activeChat,
        chatGroups,
        privateChats,
        unreadMessages,
        switchChat,
        startPrivateChat,
        handleNewMessage,
        fetchChatGroups,
        fetchPrivateChats,
        leaveGroup,
        deletePrivateChat,
        createGroupChat,
        isActiveChatLoading,
        setIsActiveChatLoading,
        clearActiveChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};